import { ForecastSlice, Maybe } from 'generated/graphql';
import { isBefore, isWeekend } from 'date-fns';

import { EditIcon } from 'assets/icons';
import { ReactElement } from 'react';
import { SpotPriceLabel } from '../../../SpotPriceLabel';
import { SpotPriceResult } from '../../../spot-price';
import clsx from 'clsx';
import { useLocale } from '@e2m-customer-portal/hooks';

interface DSTMultiCellProps {
  sliceValues?: Array<{ slice: ForecastSlice; value?: string }>;
  firstSliceDate: Date;
  showSpotPrices?: boolean;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  latestHistoricalSpotPrice?: Maybe<Date>;
  cellIndex: {
    row: number;
    column: number;
  };
  sliceIDs: Array<string>;
  editedSlices: Set<string>;
  toggleModal?: () => void;
}

export function DSTMultiCell({
  sliceValues,
  firstSliceDate,
  showSpotPrices,
  spotPrice,
  latestHistoricalSpotPrice,
  cellIndex,
  sliceIDs,
  editedSlices,
  toggleModal: toggleModal,
}: Readonly<DSTMultiCellProps>): ReactElement<DSTMultiCellProps> {
  const { code: locale } = useLocale();
  const firstSpotPrice = showSpotPrices ? spotPrice(firstSliceDate) : undefined;

  return (
    <td
      className={clsx(
        'group/cell relative box-border border-0 border-b border-solid p-0 text-center',
        cellIndex.row % 4 === 3 ? 'border-b-blue-grey-100' : 'border-b-grey-100',
        { 'bg-blue-grey-200/5': isWeekend(firstSliceDate) },
      )}
    >
      <div className="group/downtime relative box-border flex w-full items-center justify-center border-0 border-l border-solid border-grey-100 outline-2 -outline-offset-1">
        <input
          data-testid={`DST-multicell-button-${cellIndex.row}`}
          className={clsx(
            'appearance-none w-full leading-6 px-5 py-0 border-none text-center text-black -tracking-widest bg-transparent focus:outline-none',
            { 'cursor-pointer': toggleModal },
          )}
          type="button"
          value={
            sliceValues?.length && sliceValues[0].value ? `${sliceValues[0].value} / ${sliceValues[1].value}` : '/'
          }
          onClick={toggleModal}
        />
      </div>
      {firstSpotPrice?.price !== undefined && (
        <div
          className={clsx([
            'spot pointer-events-none absolute inset-y-0 left-0 flex aspect-[1.5] items-center justify-center border-0 border-r border-solid border-grey-100 text-xs',
            firstSpotPrice.gradient !== undefined ? `bg-heatmap-${firstSpotPrice.gradient}` : 'bg-grey-200',
            {
              'text-white':
                firstSpotPrice.gradient !== undefined && (firstSpotPrice.gradient < 1 || firstSpotPrice.gradient > 8),
              'saturate-50': latestHistoricalSpotPrice && isBefore(firstSliceDate, latestHistoricalSpotPrice),
              'border-l': toggleModal,
            },
          ])}
        >
          <SpotPriceLabel spotPrice={firstSpotPrice.price} unit="€" locale={locale} />
        </div>
      )}
      <div className="pointer-events-none absolute inset-y-0 right-0 z-20 flex aspect-[1.5] items-center justify-center transition-colors">
        {!!sliceIDs.filter((slice) => editedSlices.has(slice)).length && <EditIcon className="text-black/30" />}
      </div>
    </td>
  );
}
